// Premium UI with FULL DATA (no removal, only design improved)
import React, { useState, useEffect } from 'react';
import { FiUsers, FiShoppingBag, FiTrendingUp, FiRefreshCw, FiClock } from 'react-icons/fi';
import { FaRupeeSign } from "react-icons/fa";
import api from '../../utils/api';
import toast from 'react-hot-toast';

const StatCard = ({ title, value, icon, sub }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition">
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-gray-500">{title}</p>
      <div className="text-indigo-500">{icon}</div>
    </div>
    <h2 className="text-2xl font-bold text-gray-900">{value}</h2>
    <p className="text-xs text-gray-500 mt-1">{sub}</p>
  </div>
);

const Badge = ({ text }) => (
  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{text}</span>
);

const VendorOwnerDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/vendor-owner/dashboard/stats');
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amt) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const { overview, recentOrders, restaurants, topRestaurants } = data;

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500">Overview of your business</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600">
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Restaurants" value={overview.totalRestaurants} icon={<FiShoppingBag />} sub={`${overview.activeRestaurants} active`} />
        <StatCard title="Revenue" value={formatCurrency(overview.totalRevenue)} icon={<FaRupeeSign />} sub={`Month: ${formatCurrency(overview.monthRevenue)}`} />
        <StatCard title="Orders" value={overview.totalOrders} icon={<FiTrendingUp />} sub={`Today: ${overview.todayOrders}`} />
        <StatCard title="Captains" value={overview.totalCaptains} icon={<FiUsers />} sub={`${overview.totalTables} tables`} />
      </div>

      {/* Top Restaurants */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border">
        <h2 className="font-semibold mb-4">Top Restaurants</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topRestaurants?.map((r, i) => (
            <div key={i} className="p-4 border rounded-xl hover:shadow-sm">
              <div className="flex justify-between mb-2">
                <span className="font-medium">{r.restaurantName}</span>
                <Badge text={`#${i + 1}`} />
              </div>
              <p className="text-sm text-gray-500">{r.totalOrders} orders</p>
              <p className="text-sm font-semibold text-green-600">{formatCurrency(r.totalRevenue)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><FiClock /> Recent Orders</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Order</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders?.slice(0,5).map((o) => (
                <tr key={o._id} className="border-b hover:bg-gray-50">
                  <td className="py-2 font-medium text-indigo-600">#{o._id.slice(-6)}</td>
                  <td>{o.customerName}</td>
                  <td>{formatCurrency(o.totalAmount)}</td>
                  <td><Badge text={o.status} /></td>
                  <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Restaurants */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border">
        <h2 className="font-semibold mb-4">Restaurants</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {restaurants?.map((r) => (
            <div key={r._id} className="p-4 border rounded-xl hover:shadow-sm">
              <h3 className="font-medium">{r.name}</h3>
              <p className="text-sm text-gray-500">{r.city}</p>
              <div className="flex gap-2 mt-2">
                <Badge text={r.isActive ? 'Active' : 'Inactive'} />
                <Badge text={r.approvalStatus} />
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default VendorOwnerDashboard;